import React, {Component, ReactNode} from 'react'
import moment from 'moment'
import ReserveAvailabilityOvernight, {
    OvernightRentalFacilityReservation,
    ReserveAvailableStatusOvernight,
} from '../ReserveAvailabilityOvernight'
import EventEmitterExResident from '../../shared/pageBaseComponents/EventEmitterExResident'
import ReservationsRepository from '../ReservationsRepository'
import DatePickerForResident from '../components/DatePickerForResident'
import {Nullable} from '../../../app/TypeHelpers'
import {NatAsyncCallFunction} from '../../../__NoAutomatedTests/common/util/Natutil'
import {DisplayItemLabelDivNoMargin} from '../../../styledComponents/DisplayItem'
import {ReservationFlexContainer, StyledReservationDetail} from '../components/CommonStyle'
import {
    BlockSpacer,
    BlockSpacerPcOnly,
    BlockSpacerSpOnly,
    GridContainer,
    GridItem,
} from '../../../styledComponents/ResponsiveConvenience'
import {Box} from '@mui/material'
import SimpleReservationStatusTag from './SimpleReservationStatusTag'
import {ReserveAvailabilityOvernightTable} from '../components/ReserveAvailabilityOvernightTable'
import {RowFlexContainer} from '../../../styledComponents/FlexComponents'
import {
    DisplayReservationStatus,
    DisplayReservationStatusTitle,
    FacilityReserveStatusDetailButton,
} from './RRFFReserveAvailabilityViewDurationFacility'
import ResponsiveDialog from '../../shared/ResponsiveDialog'
import RFRSReservationStatusDetailForOvernightComponent
    from '../ResidentFacilityReservationStatusComponents/RFRSReservationStatusDetailForOvernightComponent'
import {NavigateFunction} from 'react-router-dom'

interface Props {
    targetDate: string

    condominiumCd: string
    facilityId: number
    facilityName: string
    reservationId: Nullable<number>
    reservationsRepo: ReservationsRepository
    eventEmitter: EventEmitterExResident

    onChangeDate: (date: string) => void
    onChangeReserveAvailabilitiesOvernight: (data: ReserveAvailabilityOvernight) => void
    navigate: NavigateFunction
    currentDateTime: moment.Moment
    holidays: string[]
}

interface State {
    reserveAvailableStatus: ReserveAvailableStatusOvernight[]
    reserveAvailabilityData: Nullable<ReserveAvailabilityOvernight>
    myReservations: OvernightRentalFacilityReservation[]
    openDetailStatusDialog: boolean
}

const SEARCH_RESERVATION_DATE_COUNT: number = 7

class RRFFReserveAvailabilityViewOvernightFacility extends Component<Props, State> {

    constructor(props: Readonly<Props>) {
        super(props)

        this.state = {
            reserveAvailableStatus: [],
            reserveAvailabilityData: null,
            myReservations: [],
            openDetailStatusDialog: false,
        }

        this.handleClickFacilityReserveStatusDetail = this.handleClickFacilityReserveStatusDetail.bind(this)
        this.handleCloseDetailStatusForOvernightDialog = this.handleCloseDetailStatusForOvernightDialog.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.updateReserveStatusAndCallback()
    }

    async componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): Promise<void> {
        if (prevProps.targetDate === this.props.targetDate) {
            return
        }
        await this.updateReserveStatusAndCallback()
    }

    async updateReserveStatusAndCallback(): Promise<void> {
        const reserveAvailabilityData = await this.getReserveAvailabilityData()
        if (reserveAvailabilityData === null) {
            return
        }

        this.setState({
            reserveAvailableStatus: reserveAvailabilityData.reserveAvailableList,
            reserveAvailabilityData: reserveAvailabilityData,
            myReservations: reserveAvailabilityData.currentReserves,
        }, () => {
            this.props.onChangeReserveAvailabilitiesOvernight(reserveAvailabilityData)
        })
    }

    async getReserveStatusForOvernight(): Promise<ReserveAvailabilityOvernight> {
        const {reservationsRepo, condominiumCd, facilityId, reservationId, targetDate} = this.props
        return await reservationsRepo.getReserveStatusForOvernightRental(
            condominiumCd,
            facilityId,
            reservationId,
            targetDate,
            SEARCH_RESERVATION_DATE_COUNT,
        )
    }

    async getReserveAvailabilityData(): Promise<Nullable<ReserveAvailabilityOvernight>> {

        try {
            await this.props.eventEmitter.showLoading()
            return await NatAsyncCallFunction(this.getReserveStatusForOvernight.bind(this), this.props.navigate)
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    handleClickFacilityReserveStatusDetail(): void {
        this.setState({
            openDetailStatusDialog: true,
        })
    }

    handleCloseDetailStatusForOvernightDialog(): void {
        this.setState({
            openDetailStatusDialog: false,
        })
    }

    renderOvernightDetailDialog(): ReactNode {
        const {facilityId, facilityName, reservationId, condominiumCd, targetDate, navigate} = this.props
        const {reserveAvailabilityData, openDetailStatusDialog} = this.state
        if (!reserveAvailabilityData) {
            return null
        }

        return (<>
            <ResponsiveDialog
                id={'pp-dialog'}
                open={openDetailStatusDialog}
                transitionDuration={(process.env.NODE_ENV === 'test') ? 0 : undefined}
                fullWidth
            >
                <RFRSReservationStatusDetailForOvernightComponent
                    condominiumCd={condominiumCd}
                    facilityId={facilityId}
                    facilityName={facilityName}
                    reservationId={reservationId}
                    searchDate={moment(targetDate)}
                    reserveAvailabilityOvernight={reserveAvailabilityData}
                    navigate={navigate}
                    onCancel={this.handleCloseDetailStatusForOvernightDialog}>
                </RFRSReservationStatusDetailForOvernightComponent>
            </ResponsiveDialog>
        </>)
    }

    render(): ReactNode {
        const {reservationId, holidays} = this.props
        return (<>
                {this.renderOvernightDetailDialog()}
                <DisplayReservationStatus>
                    <RowFlexContainer>
                        <DisplayReservationStatusTitle>
                            空き状況
                        </DisplayReservationStatusTitle>
                        <FacilityReserveStatusDetailButton
                            onClick={this.handleClickFacilityReserveStatusDetail}
                        >
                            予約状況の詳細を見る
                        </FacilityReserveStatusDetailButton>
                    </RowFlexContainer>
                    <BlockSpacer height={'10px'}/>
                    <GridContainer alignItems={'flex-start'}>
                        <GridItem over={12}>
                            <DatePickerForResident
                                date={this.props.targetDate}
                                selectableStartDate={this.props.currentDateTime}
                                onChange={this.props.onChangeDate}
                                holidays={holidays}
                            />
                        </GridItem>

                        <GridItem over={5}>
                            <BlockSpacer height={'15px'}/>
                            <ReserveAvailabilityOvernightTable
                                statuses={this.state.reserveAvailableStatus}
                                myReservations={this.state.myReservations}
                                reservationId={reservationId}
                            />
                        </GridItem>
                        <GridItem over={1}/>
                        <GridItem over={6}>
                            <BlockSpacerPcOnly height={'15px'}/>
                            <BlockSpacerSpOnly height={'20px'}/>
                            {
                                this.state.myReservations.length > 0 &&
                                <>
                                    <DisplayItemLabelDivNoMargin>あなたの予約</DisplayItemLabelDivNoMargin>
                                    {
                                        this.state.myReservations.map((myReservation, index) => {
                                            const editMode = (reservationId === myReservation.id)
                                            return (<div key={`${index}`}>
                                                <BlockSpacer height={index === 0 ? '5px' : '15px'}/>
                                                <ReservationFlexContainer>
                                                    <SimpleReservationStatusTag editMode={editMode}>
                                                        {myReservation.status.label}
                                                    </SimpleReservationStatusTag>
                                                    <Box ml={'15px'}>
                                                        <StyledReservationDetail>
                                                            {`${formatSimpleDateWithoutYearForReserve(myReservation.startDate)}から${myReservation.stayCount}泊`}
                                                        </StyledReservationDetail>
                                                        {
                                                            (myReservation.ownerName) &&
                                                            <StyledReservationDetail>
                                                                {`${myReservation.ownerName}さんが予約`}
                                                            </StyledReservationDetail>
                                                        }
                                                        {
                                                            (myReservation.title && myReservation.title !== '') &&
                                                            <StyledReservationDetail>
                                                                {myReservation.title}
                                                            </StyledReservationDetail>
                                                        }
                                                    </Box>
                                                </ReservationFlexContainer>
                                            </div>)
                                        })
                                    }
                                </>
                            }
                        </GridItem>
                    </GridContainer>
                </DisplayReservationStatus>
            </>
        )

    }
}

const MOMENT_SUFFIX_FOR_DATE = 'T00:00:00Z'

function formatSimpleDateWithoutYearForReserve(date: string): string {
    return moment(date + MOMENT_SUFFIX_FOR_DATE).format('M月D日(ddd)')
}

export default RRFFReserveAvailabilityViewOvernightFacility
